import { useState, useEffect, useCallback, useRef } from "react";
import { toast, useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import { getUserProfile, updateUserProfile } from "@/lib/stores/profileStore";
import SaveBar from "./SaveBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { User, Mail, Shield, Palette, Bell, Globe, Camera, Eye, EyeOff, Clock } from "lucide-react";
import { UserProfile, UserProfileSettings } from "@/lib/stores/storeTypes";
import { getCurrentUser } from "@/lib/stores/unifiedUserStore";
import { useTheme } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/config/supabase";
import { getScriptOptions } from "@/lib/dataSources/scriptTypes";
import { useRTL } from "@/providers/RTLProvider";
import { availableLanguages as platformLanguages } from "@/lib/translations/platformTranslations";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
type ProfileSectionProps = Record<string, never>;


export default function ProfileSection(_props: ProfileSectionProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedCompany, currentUser, refreshData } = useSimplifiedUserData();
  const { user, profile: authProfile, fetchProfile } = useAuthStore();
  const { setTheme } = useTheme();
  const { tokenInfo, manualRefresh } = useTokenRefresh();
  
  // Profile state
  const [profile, setProfile] = useState<UserProfile>({
    id: "",
    full_name: "",
    email: "",
    username: "",
    avatar_url: null,
    bio: "",
    created_at: "",
    updated_at: "",
    Last_Online: "",
    settings: {
      theme: "light",
      language: "en",
      timezone: "UTC",
      showEmail: false,
      defaultGame: "",
      defaultCompany: "",
      defaultProjectMode: "dialog",
      showLastLogin: true,
      projectUpdates: true,
      marketingEmails: false,
      twoFactorEnabled: false,
      profileVisibility: "company",
      pushNotifications: true,
      emailNotifications: true
    }
  });

  const [originalProfile, setOriginalProfile] = useState<UserProfile>(profile);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentScript, scriptMessage } = useRTL();

  // Available options
  const availableThemes = [
    { value: "light", label: "Light" },
    { value: "dark", label: "Dark" },
    { value: "auto", label: "Auto (System)" }
  ];

  const availableScripts = getScriptOptions();

  // Use languages from platform translations system
  const availableLanguages = [
    { value: "en", label: "English", flag: "🇬🇧" },
    { value: "nl", label: "Dutch", flag: "🇳🇱" },
    { value: "fr", label: "French", flag: "🇫🇷" },
    { value: "de", label: "German", flag: "🇩🇪" },
    { value: "ar", label: "Arabic", flag: "🇸🇦" },
  ];

  const availableTimezones = [
    { value: "UTC", label: "UTC" },
    { value: "America/New_York", label: "Eastern Time" },
    { value: "America/Chicago", label: "Central Time" },
    { value: "America/Denver", label: "Mountain Time" },
    { value: "America/Los_Angeles", label: "Pacific Time" },
    { value: "Europe/London", label: "London" },
    { value: "Europe/Paris", label: "Paris" },
    { value: "Europe/Berlin", label: "Berlin" },
    { value: "Asia/Tokyo", label: "Tokyo" },
    { value: "Asia/Shanghai", label: "Shanghai" }
  ];

  const loadProfileData = useCallback(async () => {
    try {
      const profile = await getCurrentUser();
      if (profile) {
        setProfile(profile);
        setOriginalProfile(profile);
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    }
  }, []);

  // Initialize profile data
  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  // Check if there are any changes
  const hasChanges = JSON.stringify(profile) !== JSON.stringify(originalProfile) || showChangePassword;

  // Handle profile field changes
  const updateProfile = (field: keyof UserProfile, value: any) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  // Handle settings field changes
  const updateSettings = async (field: keyof UserProfileSettings, value: any) => {
    setProfile(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value
      }
    }));
    
    // Apply theme immediately when changed
    if (field === 'theme') {
      setTheme(value as 'light' | 'dark' | 'auto');
    }
    
    // Apply language change immediately for instant UI update
    if (field === 'language') {
      // Automatically set the appropriate script type based on language
      let scriptType = 'latin'; // Default
      if (value === 'ar') {
        scriptType = 'arabic';
      } else if (value === 'he') {
        scriptType = 'hebrew';
      }
      
      // Update local profile state with both language and script
      setProfile(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          language: value,
          scriptType: scriptType
        }
      }));
      
      // Update the profile in the backend immediately so SimplifiedUserDataProvider picks it up
      try {
        // Send only the language and scriptType changes - backend will merge with existing settings
        const apiUpdates = {
          settings: {
            language: value,
            scriptType: scriptType
          }
        };
        
        await updateUserProfile(apiUpdates, toast);
        await fetchProfile();
        
        // Refresh the unified user data to pick up the language change
        await refreshData();
        
        // Update original profile to reflect the change
        setOriginalProfile(prev => ({
          ...prev,
          settings: {
            ...prev.settings,
            language: value,
            scriptType: scriptType
          }
        }));
        
        toast({
          title: "Language Updated",
          description: `UI language changed to ${availableLanguages.find(l => l.value === value)?.label}${scriptType !== 'latin' ? ` (${scriptType} script)` : ''}`,
        });
      } catch (error) {
        console.error("Error updating language:", error);
        toast({
          title: "Error",
          description: "Failed to update language preference",
          variant: "destructive",
        });
      }
      return; // Exit early since we handled everything
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a JPG, PNG, or GIF image",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Image must be less than 2MB",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingAvatar(true);

    try {
      // Use authenticated user from auth store
      if (!user?.id) {
        throw new Error("No user found. Please log in again.");
      }

      console.log("User authenticated:", user.id);

      // Convert file to base64 for API upload
      const base64File = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix (e.g., "data:image/png;base64,")
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Get auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error("No authentication token found");
      }
      
      console.log("Auth token found, uploading to backend...");

      // Upload via backend API
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/upload-avatar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          file: base64File,
          filename: file.name,
          mimetype: file.type
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      const publicUrl = result.data.avatar_url;

      // Update local profile state
      updateProfile('avatar_url', publicUrl);
      setOriginalProfile(prev => ({ ...prev, avatar_url: publicUrl }));

      // Refresh auth profile to sync data
      await fetchProfile();

      toast({
        title: "Success",
        description: "Avatar uploaded and saved successfully",
      });
    } catch (error: any) {
      console.error("Error uploading avatar:", error);
      toast({
        title: "Upload Failed",
        description: error?.message || "Failed to upload avatar",
        variant: "destructive",
      });
    } finally {
      setIsUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword) {
      toast({
        title: "Current Password Required",
        description: "Please enter your current password",
        variant: "destructive",
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "New password and confirmation do not match",
        variant: "destructive",
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 8 characters long",
        variant: "destructive",
      });
      return;
    }

    try {
      // First verify the current password by re-authenticating
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: profile.email,
        password: passwordData.currentPassword
      });

      if (signInError) {
        toast({
          title: "Authentication Failed",
          description: "Current password is incorrect",
          variant: "destructive",
        });
        return;
      }

      // Update password using Supabase auth
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) {
        throw error;
      }

      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setShowChangePassword(false);
      
      toast({
        title: "Success",
        description: "Password changed successfully",
      });
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to change password",
        variant: "destructive",
      });
    }
  };

  // Handle save changes
  const handleSave = async () => {
    try {
      // Prepare API update data 
      // NOTE: Username changes require a separate auth endpoint (not implemented yet)
      // For now, only profile data (full_name, avatar_url, settings) can be updated
      
      // IMPORTANT: Send ALL settings to preserve everything
      // The backend will merge these with existing settings, so we send the complete settings object
      const apiUpdates = {
        full_name: profile.full_name,
        avatar_url: profile.avatar_url,
        settings: profile.settings  // Send entire settings object - backend will merge with existing
      };

      // Update profile via store (which handles API calls)
      await updateUserProfile(apiUpdates, toast);

      // Refresh the auth store profile to get updated data
      await fetchProfile();

      // Update original profile to match current state
      setOriginalProfile(JSON.parse(JSON.stringify(profile)));

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });

    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Error",
        description: "Failed to save profile changes",
        variant: "destructive",
      });
    }
  };

  // Handle cancel changes
  const handleCancel = () => {
    setProfile(JSON.parse(JSON.stringify(originalProfile)));
    setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setShowChangePassword(false);
  };

  // Handle manual token refresh
  const handleRefreshToken = async () => {
    setIsRefreshingToken(true);
    try {
      await manualRefresh();
      toast({
        title: "Session Renewed",
        description: "Your session has been successfully renewed for another 24 hours",
      });
    } catch (error) {
      console.error("Error refreshing token:", error);
      toast({
        title: "Renewal Failed",
        description: "Failed to renew your session. Please try again or log in again if the issue persists.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshingToken(false);
    }
  };

  return (
    <div>
      <div className="max-w-3xl space-y-8">
        

        {/* Basic Information */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <User className="h-5 w-5 text-foreground" />
            <h3 className="text-lg font-medium ">{t('platform.settings.profilesection.basic_information')}</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Avatar */}
            <div className="md:col-span-2 flex items-center space-x-4">
              <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={t('platform.settings.profilesection.avatar')}
                    className="h-20 w-20 rounded-full object-cover"
                  />
                ) : ( 
                  <span className="text-2xl font-medium text-muted-foreground">
                    {currentUser.full_name?.charAt(0).toUpperCase() || currentUser.email.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center space-x-2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                >
                  <Camera className="h-4 w-4" />
                  <span>{isUploadingAvatar ? "Uploading..." : "Change Avatar"}</span>
                </Button>
                <p className="text-sm text-muted-foreground mt-1">
                  JPG, PNG or GIF. Max size 2MB.
                </p>
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label htmlFor="display-name" className="block text-sm font-medium text-muted-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="display-name"
                value={profile.full_name || ""}
                onChange={(e) => updateProfile('full_name', e.target.value)}
                placeholder={t('platform.settings.profilesection.enter_your_display_name')}
                className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-muted-foreground mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={profile.username || ""}
                onChange={(e) => updateProfile('username', e.target.value)}
                placeholder={t('platform.settings.profilesection.enter_your_username')}
                className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-muted-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                value={profile.email}
                onChange={(e) => updateProfile('email', e.target.value)}
                className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          
            {/* Bio */}
            <div className="md:col-span-2">
              <label htmlFor="bio" className="block text-sm font-medium text-muted-foreground mb-2">
                Bio
              </label>
              <textarea
                id="bio"
                value={profile.bio || ""}
                onChange={(e) => updateProfile('bio', e.target.value)}
                placeholder={t('platform.settings.profilesection.tell_us_about_yourself')}
                rows={3}
                  className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <Palette className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.profilesection.preferences')}</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Theme */}
            <div>
              <label htmlFor="theme" className="block text-sm font-medium text-muted-foreground mb-2">
                Theme
              </label>
              <select
                id="theme"
                value={profile.settings?.theme || "light"}
                onChange={(e) => updateSettings('theme', e.target.value)}
                className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {availableThemes.map(theme => (
                  <option key={theme.value} value={theme.value}>
                    {theme.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Language */}
            <div>
              <label htmlFor="language" className="block text-sm font-medium text-muted-foreground mb-2">
                Language
              </label>
              <select
                id="language"
                value={profile.settings?.language || "en"}
                onChange={(e) => updateSettings('language', e.target.value)}
                className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {availableLanguages.map(lang => (
                  <option key={lang.value} value={lang.value}>
                    {lang.flag} {lang.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                UI translations available in {platformLanguages.length} languages
              </p>
              {profile.settings?.language === 'ar' && (
                <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs">
                  <p className="text-blue-900 dark:text-blue-100">
                    <strong>💡 To type in Arabic:</strong> Switch your keyboard to Arabic input
                  </p>
                  <ul className="mt-1 text-blue-800 dark:text-blue-200 list-disc list-inside">
                    <li><strong>{t('platform.settings.profilesection.windows')}</strong> Press <kbd className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 rounded">Win + Space</kbd> to switch keyboards</li>
                    <li><strong>{t('platform.settings.profilesection.mac')}</strong> Press <kbd className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 rounded">Ctrl + Space</kbd> or <kbd className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 rounded">Cmd + Space</kbd></li>
                  </ul>
                </div>
              )}
            </div>

            {/* Timezone */}
            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-muted-foreground mb-2">
                Timezone
              </label>
              <select
                id="timezone"
                value={profile.settings?.timezone || "UTC"}
                onChange={(e) => updateSettings('timezone', e.target.value)}
                className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {availableTimezones.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Script Type / Writing System */}
            <div className="md:col-span-3">
              <label htmlFor="scriptType" className="block text-sm font-medium text-muted-foreground mb-2">
                Script / Writing System
              </label>
              <select
                id="scriptType"
                value={profile.settings?.scriptType || "latin"}
                onChange={(e) => updateSettings('scriptType', e.target.value)}
                className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {availableScripts.map(script => (
                  <option key={script.value} value={script.value}>
                    {script.label} ({script.direction.toUpperCase()})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {scriptMessage}
              </p>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <Bell className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.profilesection.notification_settings')}</h3>  
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between"> 
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.profilesection.email_notifications')}</h4>
                <p className="text-sm text-gray-500">{t('platform.settings.profilesection.receive_notifications_via_email')}</p>
              </div>
              <input
                type="checkbox"
                checked={profile.settings?.emailNotifications || false}
                onChange={(e) => updateSettings('emailNotifications', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.profilesection.push_notifications')}</h4>
                <p className="text-sm text-gray-500">{t('platform.settings.profilesection.receive_browser_push_notifications')}</p>
              </div>
              <input
                type="checkbox"
                checked={profile.settings?.pushNotifications || false}
                onChange={(e) => updateSettings('pushNotifications', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.profilesection.project_updates')}</h4>
                <p className="text-sm text-gray-500">{t('platform.settings.profilesection.get_notified_about_project_changes')}</p>
              </div>
              <input
                type="checkbox"
                checked={profile.settings?.projectUpdates || false}
                onChange={(e) => updateSettings('projectUpdates', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.profilesection.marketing_emails')}</h4>
                <p className="text-sm text-gray-500">{t('platform.settings.profilesection.receive_product_updates_and_tips')}</p>
              </div>
              <input
                type="checkbox"
                checked={profile.settings?.marketingEmails || false}
                onChange={(e) => updateSettings('marketingEmails', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
              />
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <Globe className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.profilesection.privacy_settings')}</h3>
          </div>
          
          <div className="space-y-4">
            <div>
                    <label htmlFor="profile-visibility" className="block text-sm font-medium text-muted-foreground mb-2">
                Profile Visibility
              </label>
              <select
                id="profile-visibility"
                value={profile.settings?.profileVisibility || "company"}
                onChange={(e) => updateSettings('profileVisibility', e.target.value)}
                className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="public">{t('platform.settings.profilesection.public_visible_to_everyone')}</option>
                <option value="company">{t('platform.settings.profilesection.company_visible_to_company_members')}</option>
                <option value="private">{t('platform.settings.profilesection.private_only_visible_to_you')}</option>
              </select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.profilesection.show_email_address')}</h4>
                <p className="text-sm text-gray-500">{t('platform.settings.profilesection.display_your_email_in_your_profile')}</p>
              </div>
              <input
                type="checkbox"
                checked={profile.settings?.showEmail || false}
                onChange={(e) => updateSettings('showEmail', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.profilesection.show_last_login')}</h4>
                <p className="text-sm text-gray-500">{t('platform.settings.profilesection.display_when_you_were_last_active')}</p>
              </div>
              <input
                type="checkbox"
                checked={profile.settings?.showLastLogin || false}
                onChange={(e) => updateSettings('showLastLogin', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
              />
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <Shield className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.profilesection.security')}</h3>
          </div>
          
          <div className="space-y-4">
            {/* Token Expiration */}
            {tokenInfo && (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-3 flex-1">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <h4 className="text-sm font-medium text-foreground">Session Status</h4>
                    <p className="text-sm text-muted-foreground">
                      Your session is active and will expire in{' '}
                      <span className="font-semibold text-foreground">{tokenInfo.timeUntilExpiration}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshToken}
                    disabled={isRefreshingToken || tokenInfo.isExpired}
                  >
                    {isRefreshingToken ? "Renewing..." : "Renew Session"}
                  </Button>
                  <Badge variant={tokenInfo.isExpired ? "destructive" : "default"}>
                    {tokenInfo.isExpired ? "Expired" : "Active"}
                  </Badge>
                </div>
              </div>
            )}

            {/* Password Section */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.common.password')}</h4>
                <p className="text-sm text-gray-500">
                  Change your account password
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowChangePassword(!showChangePassword)}
              >
                Change Password
              </Button>
            </div>

            {/* Change Password Form */}
            {showChangePassword && (
              <div className="bg-gray-50 p-4 rounded space-y-4">
                <div>
                  <label htmlFor="current-password" className="block text-sm font-medium text-muted-foreground mb-2">
                    Current Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      id="current-password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 border border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.current ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      id="new-password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.new ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      id="confirm-password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords.confirm ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handlePasswordChange}>
                    Update Password
                  </Button>
                </div>
              </div>
            )}

            {/* Two-Factor Authentication */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">{t('platform.settings.profilesection.two_factor_authentication')}</h4>
                <p className="text-sm text-gray-500">
                  {currentUser.settings.twoFactorEnabled ? "Enabled" : "Add an extra layer of security"}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {currentUser.settings.twoFactorEnabled && (
                  <Badge variant="secondary" className="text-xs">
                    Enabled
                  </Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateSettings('twoFactorEnabled', !(profile.settings?.twoFactorEnabled || false))}
                >
                  {profile.settings?.twoFactorEnabled ? "Disable" : "Enable"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <SaveBar
        isVisible={hasChanges}
        onSave={handleSave}
        onCancel={handleCancel}
        hasChanges={hasChanges}
      />
    </div>
  );
}
